<template>
  <v-card color="blue-grey lighten-5">
    <form-header
      :form="form"
      tour="reservation-editing-form"
      :color="blockColor()"
    >
      <template v-slot:left-buttons>
        <v-btn icon dark @click="cancel()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
      <template v-slot:title>
        <v-chip
          v-if="!!form.id"
          class="text--primary"
          small
        >
          <v-icon small>mdi-identifier</v-icon>
          {{ form.id }}
        </v-chip>
        {{ $constText("ConstReservationTypes", form.type) }}
        {{ $constText("ConstReservationStatuses", form.status) }}
      </template>
      <template v-slot:toolbar-items>
        <v-btn
          v-if="!$isMobile()"
          text
          @click="submitWithoutClose()"
          :loading="loading"
          :disabled="!initialized"
        >
          {{ $lang("main.crud.save") }}
        </v-btn>
        <v-btn
          text
          @click="submit()"
          :loading="loading"
          :disabled="!initialized"
        >
          {{ $lang("main.crud.update_and_close") }}
        </v-btn>
      </template>
    </form-header>
    <v-card-text>
      <v-container v-if="!initialized">
        <v-row>
          <v-col cols="12" xs="12" md="3">
            <v-skeleton-loader
              v-for="i in $range(3)"
              :tile="false"
              :boilerplate="false"
              type="image"
              class="mt-2"
            ></v-skeleton-loader>
          </v-col>
          <v-col cols="12" xs="12" md="9">
            <v-skeleton-loader
              :tile="false"
              :boilerplate="false"
              type="table-row-divider"
            ></v-skeleton-loader>
            <v-row>
              <v-col cols="12" xs="12" md="6" v-for="i in $range(12)">
                <v-skeleton-loader
                  :tile="false"
                  :boilerplate="false"
                  type="text"
                  class="mt-10"
                ></v-skeleton-loader>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
      <v-container v-if="initialized">
        <v-row>
          <v-col cols="12" xs="12" md="3" :class="$isMobile() ? 'px-0' : ''">
            <v-card tile :color="blockColor()">
              <v-card-text class="pb-0" id="reservations-main-controls-block">
                <div class="d-flex mb-1">
                  <v-img
                    v-if="$can('VIEW_RESERVATION_SOURCES')"
                    :src="form.source.image_url"
                    :title="form.source.human_name"
                    height="25px"
                    width="25px"
                    class="flex-grow-0 mr-2"
                  />
                  <p
                    class="text-overline white--text d-inline-flex align-self-center ma-0"
                  >
                    {{ $lang("reservations.fields.res_id") }}: {{ form.res_id }}
                    <v-icon v-if="form.is_split" small color="red"
                      >mdi-call-split</v-icon
                    >
                  </p>
                  <v-btn @click="getItem()" icon>
                    <v-icon color="white">mdi-refresh</v-icon>
                  </v-btn>
                </div>
                <v-divider color="white"></v-divider>
                <v-tooltip
                  top
                  v-if="form.type === $const('ConstReservationTypes', 'OPTION')"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      class="ma-2"
                      small
                      fab
                      color="success"
                      v-on="on"
                      @click="openConvert"
                      :loading="replaceRequest.loading"
                    >
                      <v-icon>mdi-check-decagram</v-icon>
                    </v-btn>
                  </template>
                  <span>{{
                    $lang("reservations.labels.convert_in_reservation")
                  }}</span>
                </v-tooltip>
                <v-tooltip
                  top
                  v-if="
                    form.status ===
                      $const('ConstReservationStatuses', 'PENDING') &&
                    form.type === $const('ConstReservationTypes', 'RESERVATION')
                  "
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      class="ma-2"
                      small
                      fab
                      color="success"
                      v-on="on"
                      @click="confirmReservation"
                      :loading="replaceRequest.loading"
                    >
                      <v-icon>mdi-check</v-icon>
                    </v-btn>
                  </template>
                  <span>{{
                    $lang("reservations.labels.confirm_reservation")
                  }}</span>
                </v-tooltip>
                <v-tooltip
                  top
                  v-if="
                    form.status !==
                    $const('ConstReservationStatuses', 'CANCELED')
                  "
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      id="reservation-cancel-btn"
                      class="ma-2"
                      small
                      fab
                      color="error"
                      v-on="on"
                      @click="openCancellation"
                      :loading="deletionRequest.loading"
                    >
                      <v-icon>mdi-cancel</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $lang("reservations.labels.cancel") }}</span>
                </v-tooltip>
                <v-tooltip top v-if="form.ccs_token">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      id="credit-card-btn"
                      class="ma-2"
                      small
                      fab
                      color="secondary"
                      v-on="on"
                      @click="openCardDetails()"
                    >
                      <v-icon>mdi-credit-card-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{
                    $lang("reservations.labels.view_credit_card")
                  }}</span>
                </v-tooltip>
                <v-tooltip top v-else>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      id="add-card-btn"
                      class="ma-2"
                      small
                      fab
                      color="grey"
                      v-on="on"
                      @click="openAddCard()"
                    >
                      <v-icon>mdi-credit-card-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $lang("reservations.labels.add_card") }}</span>
                </v-tooltip>
                <v-menu offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn fab small color="grey" v-on="on" class="ma-2">
                      <v-icon color="white">mdi-view-list</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="print()">
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon>mdi-printer</v-icon>
                          {{ $lang("main.words.print") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                      @click="openCardDetails()"
                      :disabled="!form.ccs_token"
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon>mdi-credit-card-outline</v-icon>
                          {{ $lang("reservations.labels.view_credit_card") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                      :disabled="
                        form.status ===
                        $const('ConstReservationStatuses', 'CANCELED')
                      "
                      @click="openCancellation"
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon color="error">mdi-cancel</v-icon>
                          {{ $lang("reservations.labels.cancel") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                      :disabled="
                        form.status !==
                          $const('ConstReservationStatuses', 'CANCELED') &&
                        $can('FORCE_DELETE_RESERVATIONS')
                      "
                      @click="openForceDelete"
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon color="error">mdi-delete-alert</v-icon>
                          {{ $lang("reservations.labels.force_delete") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                      :disabled="
                        form.status !==
                        $const('ConstReservationStatuses', 'CANCELED')
                      "
                      @click="
                        openReplace({
                          status: $const(
                            'ConstReservationStatuses',
                            'CONFIRMED'
                          )
                        })
                      "
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon color="green">mdi-restore</v-icon>
                          {{ $lang("reservations.labels.restore_reservation") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                      :disabled="
                        form.status !==
                          $const('ConstReservationStatuses', 'PENDING') &&
                        form.type ===
                          $const('ConstReservationTypes', 'RESERVATION')
                      "
                      @click="confirmReservation"
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon color="success">mdi-check</v-icon>
                          {{ $lang("reservations.labels.confirm_reservation") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                      :disabled="
                        form.type !== $const('ConstReservationTypes', 'OPTION')
                      "
                      @click="openConvert"
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon color="success">mdi-check-decagram</v-icon>
                          {{
                            $lang("reservations.labels.convert_in_reservation")
                          }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                      :disabled="!form.unit_id"
                      @click="openReplace({ unit_id: null })"
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon color="warning">mdi-bed</v-icon>
                          {{ $lang("reservations.labels.convert_to_assign") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="openSplit()">
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon>mdi-call-split</v-icon>
                          {{ $lang("reservations.title_split") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="openSplitDelete()">
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon color="red">mdi-call-split</v-icon>
                          {{ $lang("reservations.labels.split_delete") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="openLinkReservation()">
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon color="red">mdi-merge</v-icon>
                          {{ $lang("reservations.labels.link_reservation") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="isOpenChanges = true">
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon color="primary">mdi-list-box</v-icon>
                          {{ $lang("reservations.labels.changes") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-color-picker-button v-model="form.color" />
                <!-- <v-menu v-model="isColorMenuOpen" top nudge-bottom="105" nudge-left="16" :close-on-content-click="true">
                  <template v-slot:activator="{ on }">
                    <v-btn class="ma-2" small fab :color="form.color" v-on="on">
                      <v-icon color="grey">mdi-palette</v-icon>
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-text class="pa-0">
                      <v-color-picker
                        v-model="form.color"
                        show-swatches
                        hide-canvas
                        hide-sliders
                        hide-mode-switch
                        hide-inputs
                        swatches-max-height="250"
                        mode="hexa"
                      />
                    </v-card-text>
                  </v-card>
                </v-menu> -->
              </v-card-text>
              <v-card-title class="white--text pt-1">{{
                $lang("reservations.titles.period")
              }}</v-card-title>
              <v-card-text id="reservation-period-block">
                <v-date
                  v-model="form.start_date"
                  :label="$lang('reservations.fields.start_date')"
                  prepend-icon="mdi-airplane-landing"
                  dark
                  @change="dateChanged()"
                />
                <v-date
                  v-model="form.end_date"
                  :label="$lang('reservations.fields.end_date')"
                  prepend-icon="mdi-airplane-takeoff"
                  :min="form.start_date"
                  dark
                  @change="dateChanged()"
                />
                <!-- <v-datetime-picker
                  v-if="form.type === $const('ConstReservationTypes', 'OPTION')"
                  v-model="form.expired_at"
                  timeFormat="HH:mm:ss"
                  :text-field-props="{
                    prependIcon: 'mdi-calendar',
                    error: !!validation.expired_at,
                    errorMessages: !!validation.expired_at ? validation.expired_at : []
                  }"
                  :time-picker-props="{
                    format: '24hr'
                  }"
                  :ok-text="$lang('main.words.ok')"
                  :clear-text="$lang('main.words.clear')"
                  :label="$lang('reservations.fields.expiration_date')"
                >
                <template v-slot:dateIcon>
                  <v-icon>mdi-calendar</v-icon>
                </template>
                <template v-slot:timeIcon>
                  <v-icon>mdi-clock</v-icon>
                </template>
                </v-datetime-picker> -->
                <v-date-time
                  v-if="form.type === $const('ConstReservationTypes', 'OPTION')"
                  :label="$lang('reservations.fields.expiration_date')"
                  v-model="form.expired_at"
                  :error="!!validation.expired_at"
                  :errorMessages="
                    !!validation.expired_at ? validation.expired_at : []
                  "
                  dark
                />
              </v-card-text>
            </v-card>
            <v-card tile class="mt-2" id="reservation-client-block">
              <v-card-title>{{
                $lang("reservations.titles.client")
              }}</v-card-title>
              <v-card-text>
                <v-client
                  :item="form.client"
                  :errors="validation.client_id"
                  :property-id="form.property_id"
                  @changed="
                    (client) => {
                      form.client_id = client.id;
                    }
                  "
                />
                <v-expansion-panels flat accordion popout>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <span>
                        <v-icon :color="!!form.given_name ? 'warning' : ''"
                          >mdi-account-star</v-icon
                        >
                        {{ $lang("reservations.labels.guest") }}
                      </span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-0 mx-0">
                      <p class="caption">
                        {{ $lang("reservations.hints.guest") }}
                      </p>
                      <v-text-field
                        v-model="form.given_name"
                        :label="$lang('reservations.fields.given_name')"
                      />
                      <v-text-field
                        v-model="form.family_name"
                        :label="$lang('reservations.fields.family_name')"
                      />
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>
            </v-card>
            <v-card
              tile
              id="reservation-totals-block"
              class="mt-2"
              v-if="$can('VIEW_RESERVATION_AMOUNTS')"
            >
              <v-card-title>
                {{ $lang("reservations.titles.totals") }}
                <v-spacer></v-spacer>
                <v-btn @click="getItem()" icon>
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text class="pt-0">
                <v-row>
                  <v-col cols="12" xs="12" md="12" class="pt-0">
                    <v-chip
                      v-if="isInvoiceRequired"
                      label
                      small
                      color="warning text-uppercase"
                      class="ma-1"
                    >
                      <v-icon small class="mr-1">mdi-text-box-check</v-icon>
                      {{ $lang("reservations.labels.invoice_required") }}
                    </v-chip>
                    <v-chip
                      v-if="!!form.payment_method && !isShowPaymentMethods"
                      @click="isShowPaymentMethods = !isShowPaymentMethods"
                      class="ma-1 text-uppercase"
                      color="yellow"
                      small
                      label
                    >
                      <v-icon small class="mr-1">mdi-piggy-bank</v-icon>
                      {{
                        $constText(
                          "ConstReservationPaymentMethods",
                          form.payment_method
                        )
                      }}
                    </v-chip>
                    <v-select
                      v-if="isShowPaymentMethods"
                      v-model="form.payment_method"
                      :items="$in('ConstReservationPaymentMethods')"
                      :label="$lang('reservations.fields.payment_method')"
                    />
                  </v-col>
                  <v-col cols="12" xs="12" md="12" class="pt-0">
                    <v-chip
                      v-if="hasPromotions"
                      class="mr-2"
                      color="yellow text-uppercase"
                      small
                      label
                    >
                      <v-icon small class="mr-1">mdi-ticket-percent</v-icon>
                      {{ $lang("reservations.statuses.has_promotions") }}
                    </v-chip>
                  </v-col>
                  <v-col cols="12" xs="12" md="12" class="px-0 mx-0 pt-0">
                    <v-simple-table dense>
                      <template v-slot:default>
                        <tbody>
                          <tr>
                            <td>{{ $lang("reservations.fields.amount") }}</td>
                            <td>
                              <v-text-field
                                v-model="form.amount"
                                type="number"
                                hide-details
                                dense
                                :suffix="form.currency"
                                :error="!!validation.amount"
                                :error-messages="
                                  !!validation.amount ? validation.amount : []
                                "
                              ></v-text-field>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              {{ $lang("reservations.fields.cleaning_fee") }}
                            </td>
                            <td>
                              <v-text-field
                                v-model="form.cleaning_fee"
                                type="number"
                                hide-details
                                dense
                                :suffix="form.currency"
                                :error="!!validation.cleaning_fee"
                                :error-messages="
                                  !!validation.cleaning_fee
                                    ? validation.cleaning_fee
                                    : []
                                "
                              ></v-text-field>
                            </td>
                          </tr>
                          <tr>
                            <td>{{ $lang("reservations.fields.city_tax") }}</td>
                            <td>
                              <v-tooltip
                                top
                                v-if="!!legalComplianceRequests.length"
                              >
                                <template v-slot:activator="{ on }">
                                  <v-text-field
                                    v-on="on"
                                    v-model="form.city_tax"
                                    type="number"
                                    hide-details
                                    dense
                                    :suffix="form.currency"
                                    :error="!!validation.city_tax"
                                    :error-messages="
                                      !!validation.city_tax
                                        ? validation.city_tax
                                        : []
                                    "
                                  ></v-text-field>
                                </template>
                                <span>{{
                                  $lang("reservations.hints.readonly_city_tax")
                                }}</span>
                              </v-tooltip>
                              <v-text-field
                                v-else
                                v-model="form.city_tax"
                                type="number"
                                hide-details
                                dense
                                :suffix="form.currency"
                                :error="!!validation.city_tax"
                                :error-messages="
                                  !!validation.city_tax
                                    ? validation.city_tax
                                    : []
                                "
                              ></v-text-field>
                            </td>
                          </tr>
                          <tr>
                            <td>{{ $lang("reservations.fields.extras") }}</td>
                            <td class="text-right">
                              {{ $money(totalExtra, form.currency) }} +
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>{{
                                $lang("reservations.labels.total")
                              }}</strong>
                            </td>
                            <td class="text-right">
                              <strong
                                >{{
                                  $money(
                                    totalGross + parseFloat(form.city_tax),
                                    form.currency
                                  )
                                }}
                                =</strong
                              >
                            </td>
                          </tr>
                          <!-- <tr>
                            <td>
                              <strong>{{ $lang('reservations.labels.total') }}</strong>
                            </td>
                            <td class="text-right">
                              <strong>{{ $money(totalGross, form.currency) }}</strong> =
                            </td>
                          </tr> -->
                          <tr>
                            <td>
                              {{ $lang("reservations.labels.payments.title") }}
                            </td>
                            <td class="text-right">
                              {{ $money(totalPayment, form.currency) }} -
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-col>
                </v-row>
                <v-row v-if="$can('VIEW_RESERVATION_AMOUNTS')">
                  <v-col cols="12" xs="12" md="12" class="px-0 mx-0">
                    <v-simple-table dense>
                      <template v-slot:default>
                        <tbody>
                          <tr class="blue-grey lighten-5">
                            <td>
                              <strong>{{
                                $lang("reservations.labels.payments.to_pay")
                              }}</strong>
                            </td>
                            <th
                              class="text-subtitle-1 text-right"
                              :class="toPay > 0 ? 'red--text' : 'green--text'"
                            >
                              <strong>{{
                                $money(toPay, form.currency)
                              }}</strong>
                            </th>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-col>
                </v-row>
                <v-row v-if="$can('VIEW_RESERVATION_AMOUNTS')">
                  <v-col cols="12" xs="12" md="12">
                    <v-text-field
                      color="secondary"
                      v-model="form.discount"
                      :label="$lang('reservations.fields.applied_discount')"
                      type="number"
                      :suffix="form.currency"
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" md="12">
                    <v-text-field
                      color="secondary"
                      v-model="form.ota_fee"
                      :label="$lang('reservations.fields.ota_fee')"
                      type="number"
                      :suffix="form.currency"
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    xs="12"
                    md="12"
                    v-if="form.revenue_share > 0 && $can('VIEW_REVENUE_SHARES')"
                  >
                    <v-text-field
                      color="secondary"
                      v-model="form.revenue_share"
                      :label="$lang('reservations.fields.revenue_share')"
                      type="number"
                      :suffix="form.currency"
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" xs="12" md="9" class="px-0">
            <v-tabs
              v-model="tabs"
              grow
              show-arrows
              background-color="primary"
              dark
              active-class="secondary"
            >
              <v-tab id="main-tab-selector" href="#tab-main">{{
                $lang("reservations.tabs.main")
              }}</v-tab>
              <v-tab
                id="details-tab-selector"
                href="#tab-details"
                v-if="$can('VIEW_RESERVATION_AMOUNTS')"
                >{{ $lang("reservations.tabs.details") }}</v-tab
              >
              <v-tab
                id="payments-tab-selector"
                href="#tab-payments"
                v-if="$can('VIEW_RESERVATION_AMOUNTS')"
                >{{ $lang("reservations.tabs.payments") }}</v-tab
              >
              <v-tab
                id="legal-compliance-tab-selector"
                href="#tab-legal_compliance"
                v-if="$can('VIEW_LEGAL_COMPLIANCE')"
              >
                <span v-if="!form.legal_compliance_requests.length">{{
                  $lang("reservations.tabs.legal_compliance")
                }}</span>
                <v-badge
                  v-if="!!form.legal_compliance_requests.length"
                  :color="form.is_checkin_completed ? 'green' : 'accent'"
                  :content="form.legal_compliance_requests.length"
                >
                  {{ $lang("reservations.tabs.legal_compliance") }}
                </v-badge>
              </v-tab>
              <v-tab
                id="accounting-tab-selector"
                href="#tab-accounting"
                v-if="$can('VIEW_BILLS')"
                >{{ $lang("reservations.tabs.accounting") }}</v-tab
              >
              <v-tab
                id="messaging-tab-selector"
                href="#tab-messaging"
                v-if="$can('VIEW_MESSAGES')"
              >
                <span v-if="!form.messages.length">{{
                  $lang("reservations.tabs.messages")
                }}</span>
                <v-badge
                  color="accent"
                  v-if="!!form.messages.length"
                  :content="form.messages.length"
                  >{{ $lang("reservations.tabs.messages") }}</v-badge
                >
              </v-tab>

              <v-tabs-items v-model="tabs" touchless class="mt-2">
                <v-tab-item
                  id="tab-main"
                  value="tab-main"
                  class="blue-grey lighten-5"
                >
                  <v-card flat>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" xs="12" md="6" class="my-0 py-0">
                          <v-text-field
                            :value="
                              $dateDiffInDays(form.start_date, form.end_date)
                            "
                            readonly
                            :label="$lang('reservations.fields.nights')"
                            prepend-icon="mdi-weather-night"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" xs="12" md="6" class="my-0 py-0">
                          <v-row>
                            <v-col>
                              <v-text-field
                                v-model="form.arrival_time"
                                prepend-icon="mdi-clock-start"
                                :label="
                                  $lang('reservations.fields.arrival_time')
                                "
                                type="time"
                                :error="!!validation.arrival_time"
                                :error-messages="
                                  !!validation.arrival_time
                                    ? validation.arrival_time
                                    : []
                                "
                              />
                            </v-col>
                            <v-col>
                              <v-text-field
                                v-model="form.checkout_time"
                                prepend-icon="mdi-clock-end"
                                :label="
                                  $lang('reservations.fields.checkout_time')
                                "
                                type="time"
                                :error="!!validation.checkout_time"
                                :error-messages="
                                  !!validation.checkout_time
                                    ? validation.checkout_time
                                    : []
                                "
                              />
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" xs="12" md="6" class="my-0 py-0">
                          <v-text-field
                            v-model="form.guests"
                            type="number"
                            prepend-icon="mdi-human-male"
                            :label="$lang('reservations.fields.guests')"
                            :error="!!validation.guests"
                            :error-messages="
                              !!validation.guests ? validation.guests : []
                            "
                            @input="checkMaxOccupation"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" xs="12" md="3" class="my-0 py-0">
                          <v-text-field
                            v-model="form.children"
                            type="number"
                            prepend-icon="mdi-baby-face-outline"
                            :label="$lang('reservations.fields.children')"
                            :hint="$lang('reservations.hints.children')"
                            persistent-hint
                            :error="!!validation.children"
                            :error-messages="
                              !!validation.children ? validation.children : []
                            "
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" xs="12" md="3" class="my-0 py-0">
                          <v-text-field
                            v-model="form.infants"
                            type="number"
                            prepend-icon="mdi-baby-carriage"
                            :label="$lang('reservations.fields.infants')"
                            :error="!!validation.infants"
                            :error-messages="
                              !!validation.infants ? validation.infants : []
                            "
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" xs="12" md="6" class="my-0 py-0">
                          <v-autocomplete
                            v-model="form.property_id"
                            @change="propertyChanged"
                            prepend-icon="mdi-office-building"
                            :items="properties"
                            :label="$lang('reservations.fields.property')"
                            :error="!!validation.property_id"
                            :error-messages="
                              !!validation.property_id
                                ? validation.property_id
                                : []
                            "
                            :disabled="$cannot('ADVANCED_FEATURE')"
                          />
                        </v-col>
                        <v-col cols="12" xs="12" md="6" class="my-0 py-0">
                          <c-units-autocomplete
                            v-model="form.unit_id"
                            :label="$lang('reservations.fields.unit')"
                            :filters="searchUnitsFilters"
                            :options="{
                              unit_category_id: form.room_type.unit_category_id
                            }"
                            :error="!!validation.unit_id"
                            :error-messages="
                              !!validation.unit_id ? validation.unit_id : []
                            "
                          />
                        </v-col>
                      </v-row>
                      <v-row v-if="!!form.room_type_id && !!form.room_type.id">
                        <v-col cols="12" xs="12" md="6">
                          <v-row>
                            <v-col cols="12" class="pt-0 mx-0 px-0">
                              <v-img
                                gradient
                                height="120px"
                                v-if="form.room_type.unit_category.main_image"
                                :src="
                                  form.room_type.unit_category.main_image
                                    .full_path
                                "
                                width="100%"
                              >
                                <v-chip
                                  :color="form.room_type.unit_category.color"
                                  class="mt-2 ml-2 white--text text-overline elevation-10"
                                  label
                                >
                                  <v-icon color="white" small class="mr-2"
                                    >mdi-shape</v-icon
                                  >
                                  {{ form.room_type.unit_category.name }}
                                </v-chip>
                              </v-img>
                              <v-img
                                height="120px"
                                v-else
                                src="/img/room.svg"
                                width="100%"
                              >
                                <v-chip
                                  :color="form.room_type.unit_category.color"
                                  class="mt-2 ml-2 white--text text-overline elevation-10"
                                  label
                                >
                                  <v-icon color="white" small class="mr-2"
                                    >mdi-shape</v-icon
                                  >
                                  {{ form.room_type.unit_category.name }}
                                </v-chip>
                              </v-img>
                            </v-col>
                          </v-row>
                        </v-col>
                        <v-col cols="12" xs="12" md="6">
                          <v-text-field
                            v-model="form.room_type.name"
                            prepend-icon="mdi-cash-multiple"
                            :label="$lang('reservations.fields.room_type')"
                            readonly
                            append-outer-icon="mdi-swap-horizontal"
                            @click:append-outer="changeRoomType()"
                          ></v-text-field>
                          <v-row>
                            <v-col>
                              <c-refundable-menu
                                v-model="form.is_refundable"
                                :label="$lang('main.words.refundability')"
                                x-small
                                block
                              />
                            </v-col>
                            <v-col>
                              <c-meal-plan-menu
                                v-model="form.meal_plan"
                                :label="$lang('main.words.meal_plan')"
                                x-small
                                block
                              />
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                      <v-row v-if="!form.room_type_id">
                        <v-col cols="12" xs="12" md="6"></v-col>
                        <v-col cols="12" xs="12" md="6">
                          <c-roomtypes-autocomplete
                            v-model="form.room_type_id"
                            prepend-icon="mdi-cash-multiple"
                            :filters="{
                              property_id: form.property_id
                            }"
                            :label="$lang('reservations.fields.room_type')"
                            :error="!!validation.room_type_id"
                            :error-messages="
                              !!validation.room_type_id
                                ? validation.room_type_id
                                : []
                            "
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col
                          cols="12"
                          xs="12"
                          md="6"
                          v-if="$can('VIEW_RESERVATION_INTERNAL_NOTES')"
                        >
                          <v-textarea
                            v-model="form.internal_note"
                            :label="$lang('reservations.fields.internal_note')"
                            :hint="$lang('reservations.hints.internal_note')"
                            filled
                            :rows="$isMobile() ? 5 : 3"
                            :error="!!validation.internal_note"
                            :error-messages="
                              !!validation.internal_note
                                ? validation.internal_note
                                : []
                            "
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          xs="12"
                          md="6"
                          v-if="$can('VIEW_RESERVATION_SERVICE_NOTES')"
                        >
                          <v-textarea
                            v-model="form.service_note"
                            :label="$lang('reservations.fields.service_note')"
                            :hint="$lang('reservations.hints.service_note')"
                            filled
                            :rows="$isMobile() ? 5 : 3"
                            :error="!!validation.service_note"
                            :error-messages="
                              !!validation.service_note
                                ? validation.service_note
                                : []
                            "
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" class="mt-0 py-0">
                          <v-textarea
                            v-model="form.ota_note"
                            :label="$lang('reservations.fields.ota_note')"
                            :hint="$lang('reservations.hints.ota_note')"
                            filled
                            background-color="amber lighten-4"
                            :error="!!validation.ota_note"
                            :error-messages="
                              !!validation.ota_note ? validation.ota_note : []
                            "
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" xs="12" md="6" class="py-0">
                          <v-boolean
                            v-model="form.is_dont_move"
                            prepend-icon="mdi-cancel"
                            :label="$lang('reservations.fields.is_dont_move')"
                            :hint="$lang('reservations.hints.is_dont_move')"
                            persistent-hint
                            :error="!!validation.is_dont_move"
                            :error-messages="
                              !!validation.is_dont_move
                                ? validation.is_dont_move
                                : []
                            "
                          />
                        </v-col>
                        <v-col cols="12" xs="12" md="6" class="py-0">
                          <v-text-field
                            v-model="form.pin"
                            prepend-icon="mdi-key"
                            :label="$lang('reservations.fields.pin')"
                            :hint="$lang('reservations.hints.pin')"
                            :error="!!validation.pin"
                            :error-messages="
                              !!validation.pin ? validation.pin : []
                            "
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" xs="12" md="6" class="py-0">
                          <v-text-field
                            v-model="form.flight_number"
                            prepend-icon="mdi-airplane"
                            append-icon="mdi-eye"
                            :label="$lang('reservations.fields.flight_number')"
                            :hint="$lang('reservations.hints.flight_number')"
                            :error="!!validation.flight_number"
                            :error-messages="
                              !!validation.flight_number
                                ? validation.flight_number
                                : []
                            "
                            @click:prepend="openFlight"
                            @click:append="openFlight"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row v-if="$can('INTERNAL_SUPPORT')" class="admin">
                        <v-col cols="3">
                          <v-select
                            v-model="form.type"
                            :items="$in('ConstReservationTypes')"
                          />
                        </v-col>
                        <v-col cols="3">
                          <v-boolean
                            v-model="form.is_hidden"
                            label="Is Hidden"
                          ></v-boolean>
                        </v-col>
                        <v-col cols="3">
                          <v-select
                            v-model="form.payment_method"
                            :items="$in('ConstReservationPaymentMethods')"
                            label="Payment method"
                          />
                        </v-col>
                        <v-col cols="3">
                          <v-text-field
                            v-model="form.nuki_smart_lock_auth_id"
                            label="Nuki Auth id"
                          />
                        </v-col>
                        <v-col cols="3">
                          <v-select
                            v-model="form.status"
                            :items="$in('ConstReservationStatuses')"
                            label="Status"
                          />
                        </v-col>
                        <v-col cols="3">
                          <c-reservation-sources
                            v-model="form.source_id"
                            :label="$lang('reservations.fields.source_id')"
                            :defaultSelection="true"
                            :singleLine="true"
                            :hideDetails="true"
                          />
                        </v-col>
                        <v-col cols="3">
                          <v-text-field
                            v-model="form.is_split"
                            :label="$lang('reservations.fields.is_split')"
                            hint="0 no, 1 split, 2 moved"
                          />
                        </v-col>
                        <v-col cols="3">
                          <v-text-field v-model="form.res_id" label="res_id" />
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
                <v-tab-item
                  id="tab-details"
                  value="tab-details"
                  class="blue-grey lighten-5"
                >
                  <v-card flat class="mb-2">
                    <v-card-text>
                      <v-container>
                        <v-row>
                          <v-col cols="12" md="3">
                            <v-text-field
                              v-model="createdDateTime"
                              :label="$lang('main.crud.created_at')"
                              readonly
                              dense
                              hide-details
                            />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field
                              v-model="updatedDateTime"
                              :label="$lang('main.crud.updated_at')"
                              readonly
                              dense
                              hide-details
                            />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field
                              v-model="form.creator.name"
                              :label="$lang('main.crud.created_by')"
                              readonly
                              dense
                              hide-details
                            />
                          </v-col>
                          <v-col cols="12" md="3">
                            <c-agencies-autocomplete
                              v-if="$can('VIEW_AGENCIES')"
                              v-model="form.ref"
                              :label="$lang('reservations.fields.referral')"
                              :options="agenciesFilter"
                              dense
                              hide-details
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                  </v-card>
                  <v-row v-if="item.linked.length > 1">
                    <v-col cols="12" xs="12" md="12">
                      <v-card flat>
                        <v-card-text>
                          <h2>
                            {{
                              $lang("reservations.labels.linked_reservations")
                            }}
                          </h2>
                          <v-simple-table>
                            <template v-slot:default>
                              <thead>
                                <tr>
                                  <th>
                                    {{ $lang("reservations.fields.room_type") }}
                                  </th>
                                  <th>
                                    {{ $lang("reservations.fields.unit") }}
                                  </th>
                                  <th>
                                    {{ $lang("reservations.fields.amount") }}
                                  </th>
                                  <th>{{ $lang("main.crud.actions") }}</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(linked, l) in item.linked" :key="l">
                                  <td>{{ linked.room_type.name }}</td>
                                  <td>
                                    <span v-if="!!linked.unit_id">{{
                                      linked.unit.name
                                    }}</span>
                                    <v-chip
                                      v-if="!linked.unit_id"
                                      label
                                      x-small
                                      color="warning"
                                    >
                                      <v-icon x-small class="mr-1"
                                        >mdi-bed</v-icon
                                      >{{
                                        $lang("reservations.labels.to_assign")
                                      }}
                                    </v-chip>
                                  </td>
                                  <td>
                                    {{ $money(linked.amount, form.currency) }}
                                  </td>
                                  <td>
                                    <v-btn
                                      icon
                                      :disabled="linked.id === itemId"
                                      @click="viewReservation(linked.id)"
                                    >
                                      <v-icon>mdi-eye</v-icon>
                                    </v-btn>
                                  </td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                  <reservation-promotions
                    v-if="hasPromotions"
                    :options="{
                      withTitle: true,
                      withActions: false,
                      withFilters: false,
                      withPropertyName: false,
                      filters: {
                        reservation_id: itemId
                      }
                    }"
                    class="my-2"
                  />
                  <reservation-extras-list
                    v-if="$can('VIEW_RESERVATION_EXTRAS')"
                    :reservation-id="itemId"
                    :currency="form.currency"
                    :reservation="{
                      start_date: form.start_date,
                      end_date: form.end_date,
                      guests: form.guests,
                      children: form.children,
                      property_id: form.property_id
                    }"
                    class="my-2"
                    reset-filters
                    @refreshParent="getItem()"
                  />
                  <v-row>
                    <v-col cols="12" xs="12" md="4" class="pa-0">
                      <amount-details
                        :reservation-id="itemId"
                        @refreshParent="getItem()"
                      />
                    </v-col>
                    <v-col cols="12" xs="12" md="8" class="py-0 pl-0 mt-1">
                      <reservation-housekeeping
                        v-if="$can('VIEW_HOUSEKEEPING')"
                        :reservation-id="itemId"
                        :unit-id="form.unit_id"
                        :with-filters="false"
                        class="my-2"
                      />
                    </v-col>
                  </v-row>
                  <reservation-unit-category-costs
                    v-if="$can('VIEW_COSTS')"
                    :reservation-id="itemId"
                    :currency="form.currency"
                    :reservation="{
                      property_id: form.property_id
                    }"
                    class="my-2"
                  />
                  <reservation-meta :reservation-id="itemId" />
                </v-tab-item>
                <v-tab-item
                  id="tab-payments"
                  value="tab-payments"
                  class="blue-grey lighten-5"
                >
                  <v-row>
                    <v-col cols="12" xs="12" class="ma-0 pa-0">
                      <payments-list
                        v-if="$can('VIEW_PAYMENTS')"
                        @refreshParent="getItem()"
                        :limit="5"
                        :owner-id="itemId"
                        :owner-type="
                          $const('ConstMorphTypeAliases', 'RESERVATION')
                        "
                        :options="{
                          reservation: form
                        }"
                      />
                    </v-col>
                    <v-col
                      v-if="$can('BETA_FEATURE')"
                      cols="12"
                      xs="12"
                      class="ma-0 pa-0"
                    >
                      <payments-planned-list
                        class="pt-0"
                        v-if="$can('VIEW_PAYMENTS')"
                        :reservation-id="itemId"
                        :currency="form.currency"
                        :reservation="{
                          start_date: form.start_date,
                          end_date: form.end_date
                        }"
                        :with-filters="false"
                        :show-view="false"
                        :show-title="true"
                        :columns-to-show="[
                          'type',
                          'status',
                          'amount',
                          'sends',
                          'actions'
                        ]"
                      />
                    </v-col>
                  </v-row>
                </v-tab-item>
                <v-tab-item
                  v-if="$can('VIEW_LEGAL_COMPLIANCE')"
                  id="tab-legal_compliance"
                  value="tab-legal_compliance"
                  class="blue-grey lighten-5"
                >
                  <v-card class="mb-2" flat>
                    <v-card-text>
                      <v-row>
                        <v-col cols="8">
                          <v-boolean
                            v-model="form.is_checkin_completed"
                            :label="
                              $lang('reservations.fields.is_checkin_completed')
                            "
                            :hint="
                              $lang('reservations.hints.is_checkin_completed')
                            "
                            persistent-hint
                          />
                        </v-col>
                        <v-col cols="4" align-self="center">
                          <v-btn
                            :href="
                              $customerPortalUrl(
                                `portal/reservations?token=${form.client.nano_id}`
                              )
                            "
                            target="_blank"
                            :disabled="!form.client_id"
                          >
                            <v-icon>mdi-web-box</v-icon>
                            {{ $lang("clients.labels.customer_portal") }}
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                  <legal-compliance-requests-card-list
                    :reservation-id="itemId"
                    :unit-id="form.unit_id"
                    :form-options="{
                      start_date: form.start_date,
                      end_date: form.end_date
                    }"
                    :withFilters="false"
                    :resetFilters="true"
                  />
                </v-tab-item>
                <v-tab-item
                  id="tab-accounting"
                  value="tab-accounting"
                  class="blue-grey lighten-5"
                >
                  <v-chip
                    v-if="isInvoiceRequired"
                    label
                    small
                    color="warning text-uppercase"
                    class="ma-1"
                  >
                    <v-icon small class="mr-1">mdi-text-box-check</v-icon>
                    {{ $lang("reservations.labels.invoice_required") }}
                  </v-chip>
                  <bills-list
                    :options="{
                      noImport: true
                    }"
                    :with-filters="false"
                    :reservation-id="itemId"
                    @refreshParent="getItem()"
                    reset-filters
                  >
                    <template v-slot:actions-left>
                      <h2>{{ $lang("bills.title") }}</h2>
                    </template>
                  </bills-list>
                  <documents-list
                    :options="{
                      noImport: true
                    }"
                    :with-filters="false"
                    :with-actions="false"
                    :reservation-id="itemId"
                    @refreshParent="getItem()"
                    reset-filters
                  >
                    <template v-slot:actions-left>
                      <h2 class="my-2">{{ $lang("documents.title") }}</h2>
                    </template>
                  </documents-list>
                  <owner-documents-list
                    v-if="!!item.property.property_owner_id"
                    :reservation-id="itemId"
                    :with-filters="false"
                    :with-actions="false"
                    @refreshParent="getItem()"
                    reset-filters
                  >
                    <template v-slot:actions-left>
                      <h2 class="my-2">{{ $lang("owner-documents.title") }}</h2>
                    </template>
                  </owner-documents-list>
                </v-tab-item>
                <v-tab-item id="tab-messaging" value="tab-messaging">
                  <v-card flat>
                    <v-card-text>
                      <reservation-messages
                        :client="form.client"
                        :property="form.property"
                        :reservation-id="itemId"
                        :thread-id="form.thread_id"
                      />
                    </v-card-text>
                  </v-card>
                </v-tab-item>
              </v-tabs-items>
            </v-tabs>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <v-dialog
      v-model="isOpenCardDetails"
      transition="dialog-bottom-transition"
      width="700"
    >
      <card-details
        v-if="isOpenCardDetails"
        :data="{
          ccs_token: form.ccs_token,
          api: {
            phone: form.property.mobile,
            email: form.property.email,
            property_id: form.property.id,
            user: form.property.secom_api_user
          }
        }"
        @cancel="closeCardDetails"
        @success="closeCardDetails"
        @addCard="openAddCard"
      />
    </v-dialog>

    <v-dialog
      v-model="isOpenSplit"
      transition="dialog-bottom-transition"
      width="30%"
      :fullscreen="$isMobile()"
    >
      <split-form
        v-if="isOpenSplit"
        :item-id="itemId"
        @cancel="closeSplit"
        @error="failedSplit"
        @success="successSplit"
        :options="{
          reservation: form
        }"
      />
    </v-dialog>

    <v-dialog
      v-model="isOpenLinkReservation"
      transition="dialog-bottom-transition"
      width="30%"
    >
      <link-reservation-form
        v-if="isOpenLinkReservation"
        :reservation="form"
        @saved="closeLinkReservation"
        @cancel="isOpenLinkReservation = false"
      />
    </v-dialog>

    <v-dialog
      v-model="isOpenAddCard"
      transition="dialog-bottom-transition"
      width="50rem"
    >
      <add-card-form
        v-if="isOpenAddCard"
        :reservation-id="itemId"
        :property-id="form.property.id"
        @saved="closeAddCard"
        @cancel="closeAddCard"
      />
    </v-dialog>

    <cancellation-dialog
      v-model="isOpenCancellation"
      :reservation-id="itemId"
      :res-id="form.res_id"
      @closed="isOpenCancellation = false"
      @deleted="reservationDeleted"
    />

    <v-confirmation
      v-model="isOpenConvert"
      :options="{
        loading: replaceRequest.loading,
        yes: () => convertReservation()
      }"
      :title="$lang('reservations.labels.convert_in_reservation')"
      :width="'50%'"
    />

    <v-confirmation
      v-model="isOpenReplace"
      :options="{
        loading: replaceRequest.loading,
        yes: () => replaceReservation(replaceOptions)
      }"
    />

    <v-confirmation
      v-model="isOpenForceDelete"
      :options="{
        loading: replaceRequest.loading,
        yes: () => forceDeleteReservation()
      }"
    />

    <v-confirmation
      v-model="isOpenSplitDelete"
      :options="{
        loading: splitDeleteRequest.loading,
        yes: () => splitDelete(itemId)
      }"
    />

    <v-confirmation
      v-model="isOpenDateChanged"
      :options="{
        loading: amountRefreshing,
        yes: () => refreshAmount()
      }"
      :title="$lang('reservations.labels.date_changed')"
      :width="'50%'"
    />

    <Print :item="form" :initialized="initialized" ref="print" />
    <v-dialog v-model="isOpenChanges" width="80vw">
      <changes
        v-if="isOpenChanges"
        :id="itemId"
        @close="isOpenChanges = false"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import MixpanelEvents from "@/constants/events";

import form from "@/pages/reservations/mixins/form";
import cards from "@/pages/reservations/mixins/cards";
import totals from "@/pages/reservations/mixins/totals";

const LegalComplianceRequestsCardList = () =>
  import(
    /*webpackChunkName: "legal-compliance" */ "@/pages/legal-compliance-requests/CardsList"
  );
const ReservationExtrasList = () =>
  import(
    /*webpackChunkName: "reservations" */ "@/pages/reservation-extras/List"
  );
const ReservationUnitCategoryCosts = () =>
  import(
    /*webpackChunkName: "reservations" */ "@/pages/reservation-unit-category-costs/List"
  );
const ReservationHousekeeping = () =>
  import(/*webpackChunkName: "reservations" */ "@/pages/housekeeping/List");
const ReservationPromotions = () =>
  import(
    /*webpackChunkName: "reservations" */ "@/pages/property-promotions/List"
  );
const ReservationDetails = () =>
  import(
    /*webpackChunkName: "reservations" */ "@/pages/reservations/partials/Details"
  );
const AmountDetails = () =>
  import(
    /*webpackChunkName: "reservations" */ "@/pages/reservations/partials/AmountDetails"
  );
const PaymentsList = () =>
  import(/*webpackChunkName: "reservations" */ "@/pages/payments/List");
const PaymentsPlannedList = () =>
  import(/*webpackChunkName: "reservations" */ "@/pages/payments-planned/List");
const ReservationMeta = () =>
  import(
    /*webpackChunkName: "reservations" */ "@/pages/reservations/meta/List.vue"
  );
const AddCardForm = () =>
  import(
    /*webpackChunkName: "reservations" */ "@/pages/reservations/partials/AddCardForm"
  );
const LinkReservationForm = () =>
  import(
    /*webpackChunkName: "reservations" */ "@/pages/reservations/partials/LinkReservationForm"
  );
const ReservationMessages = () =>
  import(/*webpackChunkName: "reservations" */ "@/pages/messages/Messages");
const BillsList = () =>
  import(/*webpackChunkName: "accounting" */ "@/pages/bills/List");
const DocumentsList = () =>
  import(/*webpackChunkName: "accounting" */ "@/pages/documents/List");
const OwnerDocumentsList = () =>
  import(/*webpackChunkName: "accounting" */ "@/pages/owner-documents/List");
const Print = () =>
  import(
    /*webpackChunkName: "reservations" */ "@/pages/reservations/partials/Print"
  );
const CancellationDialog = () =>
  import(
    /*webpackChunkName: "reservations" */ "@/pages/reservations/partials/CancellationDialog"
  );
const Changes = () =>
  import(
    /*webpackChunkName: "reservations" */ "@/pages/reservations/partials/Changes"
  );

import CardDetails from "@/pages/reservations/partials/CardDetails";
import SplitForm from "@/pages/reservations/SplitForm";

import {
  UPDATE_RESERVATIONS,
  UPDATE_RESERVATIONS_RESET,
  GET_RESERVATIONS_SINGLE,
  REPLACE_RESERVATIONS,
  FORCE_REMOVE_RESERVATIONS
} from "@/store/reservations/actions";

import { GET_ROOM_TYPES_LIST } from "@/store/room-types/actions";

export default {
  name: "EditingForm",
  mixins: [form, cards, totals],
  components: {
    LegalComplianceRequestsCardList,
    ReservationExtrasList,
    ReservationUnitCategoryCosts,
    ReservationHousekeeping,
    ReservationPromotions,
    ReservationMeta,
    AmountDetails,
    PaymentsList,
    PaymentsPlannedList,
    BillsList,
    DocumentsList,
    OwnerDocumentsList,
    CardDetails,
    ReservationDetails,
    ReservationMessages,
    SplitForm,
    AddCardForm,
    LinkReservationForm,
    Print,
    CancellationDialog,
    Changes
  },
  props: {
    itemId: {
      type: Number,
      required: true
    },
    openTab: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isColorMenuOpen: false,
      isOpenLinkReservation: false,
      isShowPaymentMethods: false,
      isOpenChanges: false
    };
  },
  beforeMount() {
    this.getProperties();
    this.getItem();

    if (this.openTab) {
      this.tabs = this.openTab;
    }
  },
  methods: {
    getItem() {
      this.$store.dispatch(GET_RESERVATIONS_SINGLE, {
        id: this.itemId,
        params: {
          editing: {
            with_messages: true,
            with_linked: true,
            with_legal_compliance_request: true
          }
        }
      });
    },
    getRoomTypes() {
      this.$store.dispatch(GET_ROOM_TYPES_LIST, {
        sortBy: "name",
        order: "asc",
        property_id: this.form.property_id
      });
    },
    resetAction() {
      return UPDATE_RESERVATIONS_RESET;
    },
    submitAction() {
      return UPDATE_RESERVATIONS;
    },
    submitActionOptions() {
      return {
        id: this.itemId,
        params: this.prepare()
      };
    },
    populate(item) {
      if (!item.color) {
        item.color = "";
      }

      this.form = { ...item };
      this.form.expired_at = this.$toDateTimeFormat(
        this.form.expired_at,
        "YYYY-MM-DD HH:mm:ss"
      );
    },
    prepare() {
      this.form.total_gross = this.totalGross;
      let data = { ...this.form };

      delete data["unit"];
      delete data["client"];
      delete data["source"];
      delete data["property"];
      delete data["legal_compliance_requests"];
      delete data["extras"];
      delete data["payment"];
      delete data["to_pay"];
      delete data["ccs_token"];

      return data;
    },
    track() {
      this.$monitorService.track(MixpanelEvents.RESERVATION_EDITED);
    },
    reservationDeleted() {
      this.$emit("success");
    },
    convertReservation() {
      this.$store
        .dispatch(REPLACE_RESERVATIONS, {
          id: this.itemId,
          params: {
            type: this.$const("ConstReservationTypes", "RESERVATION"),
            expired_id: null,
            status: this.$const("ConstReservationStatuses", "CONFIRMED")
          }
        })
        .then((response) => {
          this.$emit("success", response);
        });
    },
    reservationReplace(options) {
      this.$store
        .dispatch(REPLACE_RESERVATIONS, {
          id: this.itemId,
          params: { ...options }
        })
        .then((response) => {
          this.$emit("success", response);
        });
    },
    confirmReservation() {
      this.$store
        .dispatch(REPLACE_RESERVATIONS, {
          id: this.itemId,
          params: {
            status: this.$const("ConstReservationStatuses", "CONFIRMED")
          }
        })
        .then((response) => {
          this.$emit("success", response);
        });
    },
    forceDeleteReservation() {
      this.$store
        .dispatch(FORCE_REMOVE_RESERVATIONS, {
          id: this.itemId
        })
        .then((response) => {
          this.$emit("success", response);
        });
    },
    viewReservation(id) {
      this.$toRoute({
        name: "reservation-view",
        params: {
          itemId: id
        }
      });
    },
    propertyChanged(id) {
      if (!id) {
        return;
      }

      //this.getRoomTypes();
    },
    changeRoomType() {
      //this.getRoomTypes();
      this.form.room_type_id = null;
    },
    openLinkReservation() {
      this.isOpenLinkReservation = true;
    },
    closeLinkReservation() {
      this.isOpenLinkReservation = false;
      this.getItem();
    },
    openFlight() {
      if (!this.form.flight_number) {
        return;
      }

      const flightStart = this.form.flight_number.slice(0, 2);
      const flightEnd = this.form.flight_number.slice(2);
      const link = `https://www.flightstats.com/v2/flight-tracker/${flightStart}/${flightEnd}`;

      window.open(link);
    },
    print() {
      this.$refs.print.print();
    },
    checkMaxOccupation() {
      if (
        !this.form.room_type_id ||
        this.form.guests <= this.form.room_type.unit_category.max_occupation
      ) {
        return;
      }

      this.showNotification(
        this.$lang("reservations.labels.max_occupation_exceed"),
        "warning"
      );
    }
  },
  computed: {
    ...mapState({
      item: (state) => state.reservations.getSingleRequest.item,
      loading: (state) => state.reservations.editingRequest.loading,
      validation: (state) => state.reservations.editingRequest.validation,
      itemRequest: (state) => state.reservations.getSingleRequest,
      deletionRequest: (state) => state.reservations.deletionRequest,
      replaceRequest: (state) => state.reservations.replaceRequest,
      roomTypes: (state) => state.roomTypes.getListRequest.list
    }),
    initialized() {
      return !this.itemRequest.loading;
    },
    createdDateTime() {
      if (!this.form.created_at) {
        return null;
      }

      return this.$toDateTimeFormat(this.form.created_at);
    },
    updatedDateTime() {
      if (!this.form.updated_at) {
        return null;
      }

      return this.$toDateTimeFormat(this.form.updated_at);
    },
    legalComplianceRequests() {
      if (!this.item.legal_compliance_requests) {
        return [];
      }

      return this.item.legal_compliance_requests;
    },
    hasPromotions() {
      return (
        !!this.item.reservation_promotions &&
        !!this.item.reservation_promotions.length
      );
    },
    agenciesFilter() {
      return {
        filters: {
          property_id: this.form.property_id
        }
      };
    },
    isInvoiceRequired() {
      if (!this.form.client) {
        return false;
      }

      return this.form.client.type == this.$const("ConstClientType", "COMPANY");
    }
  }
};
</script>

<style scoped>
.check-in-out-dates {
  background-color: #424242;
}
.table-total-label {
  width: 45%;
}
.ota-notes {
  font-size: 12px;
  line-height: 12px;
}
</style>
